import {AGENCY_KEYS} from "@/api/agencyApi";
import {USER_KEYS} from "@/api/userApi";
import type {SubBrandInviteSerializer} from "@/generated/Company";
import type {ReferredInfluencerSerializer} from "@/generated/Influencer";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import i18n from "@/i18n";
import {queryClient} from "@/queryClient";
import type {
  TAccountMember,
  TCompanyProfile,
  TDashboardCampaign,
  TGroupInfluencer,
  TInfiniteQueryOptions,
  TPermission,
  TQueryOptions,
  TQueryResultWithCount,
  TSingleBrandCustomer,
} from "@/store/types";
import useUserStore from "@/store/user";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import snakeCase from "lodash/snakeCase";
import {toast} from "react-toastify";

export const COMPANY_KEYS = {
  mCompany: (companyId: number) => [`/company/${companyId}/`],
  mCompanyAdditionalLimitRequest: (subBrandId: number) => [`/sub_brand/${subBrandId}/request_additional_limit/`],
  mCompanyDraftCampaignsDelete: (companyId: number) => [`/company/${companyId}/delete_draft_campaigns/`],
  mCompanyFavouriteInfluencer: (companyId: number) => [`/company/${companyId}/favourite_influencer/`],
  mCompanyMemberInvite: (companyId: number) => [`/sub_brand/${companyId}/invite_user/`],
  mCompanyMemberInviteRemoval: (companyId: number) => [`/sub_brand/${companyId}/remove_invite/`],
  mCompanyMemberPermissionUpdate: (companyId: number) => [`/sub_brand/${companyId}/update_permission/`],
  mCompanyMemberPermissionUpdateRequest: (companyId: number) => [`/sub_brand/${companyId}/request_permission_upgrade/`],
  mCompanyMemberPermissionUpdateResponse: (companyId: number) => [
    `/sub_brand/${companyId}/respond_permission_upgrade/`,
  ],
  mCompanyMemberRegistration: (companyId: number) => [`/sub_brand/${companyId}/register_sub_brand_user/`],
  mCompanyMemberRemoval: (companyId: number) => [`/sub_brand/${companyId}/remove_user/`],
  mCompanyRanking: (companyId: number) => [`/company/${companyId}/update_ranking/`],
  qCompany: (companyId: number) => [`/company/${companyId}/`],
  qCompanyCampaigns: (params?: {companyId?: number; status: "active" | "open" | "complete" | "paused" | "drafts"}) => [
    `/company/${params?.companyId}/${params?.status ?? "all"}/`,
  ],
  qCompanyChatCampaigns: (companyId: number) => [`/company/${companyId}/chat_campaigns/`],
  qCompanyCollaborations: (companyId: number, params?: string) => [`/company/${companyId}/collaborations/?${params}`],
  qCompanyDetails: (companyId: number) => [`/company/${companyId}/details/`],
  qCompanyFavouriteContent: (company_profile_id: number, params: string) => [
    `/agency/favourite_content/?company_profile_id=${company_profile_id}${params}`,
  ],
  qCompanyFavouriteContentStats: (company_profile_id: number) => [
    `/agency/favourite_content_stats/?company_profile_id=${company_profile_id}`,
  ],
  qCompanyFollowers: (companyId: number, params?: string) => [`/company/${companyId}/followers/?${params}`],
  qCompanyInvites: (companyId: number) => [`/sub_brand/${companyId}/invites/`],
  qCompanyList: (params?: string) => [`/sub_brand/?subscriptions=true&${params}`],
  qCompanyMembers: (companyId: number) => [`/sub_brand/${companyId}/members/`],
  qCompanyNotificationCounts: (companyId: number) => [`/company/${companyId}/message_counts/`],
  qCompanyOpenCampaigns: (companyId: number) => [`/company/${companyId}/open_campaigns/`],
  qCompanyRatings: (companyId: number, params?: string) => [`/company/${companyId}/ratings/?${params}`],
  qCompanyRecommendedInfluencers: (companyId: number) => [`/company/${companyId}/recommended_influencers/`],
  qCompanyReferrals: (companyId: number) => [`/company/${companyId}/referred/`],
} as const;

export const useInviteCompanyMemberMutation = ({
  companyId,
  email,
  selectedPermission,
  onSuccessCallback,
}: {
  companyId: number;
  email: string;
  selectedPermission: TPermission;
  onSuccessCallback?: () => void;
}) =>
  useMutation({
    mutationKey: COMPANY_KEYS.mCompanyMemberInvite(companyId),
    mutationFn: () =>
      client.post(COMPANY_KEYS.mCompanyMemberInvite(companyId)[0], {
        emails_data: email,
        permission: selectedPermission,
      }),
    onError: (error: AxiosError<{error: string}>) => handleErrorWithToast(error),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyInvites(companyId)});
      onSuccessCallback?.();
    },
  });

export const useUpdateCompanyMemberPermissionMutation = ({
  companyMemberUserId,
  companyId,
}: {
  companyMemberUserId: number;
  companyId: number;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("multi_account:permission_update_success")},
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdate(companyId),
    mutationFn: ({newPermission}: {newPermission: TPermission}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdate(companyId)[0], {
        user_id: companyMemberUserId,
        permission: newPermission,
        action: "set",
      }),
    onSuccess: () => {
      const userId = Number(useUserStore.getState().user.id);
      [COMPANY_KEYS.qCompanyMembers(companyId), USER_KEYS.qUser(userId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });

export const useRequestPermissionUpgradeMutation = ({
  companyId,
  onSuccessCallback,
}: {
  companyId: number;
  onSuccessCallback?: () => void;
}) =>
  useMutation({
    meta: {successMessage: i18n.t("multi_account:permission_request_success")},
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdateRequest(companyId),
    mutationFn: ({newPermission}: {newPermission: TPermission}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdateRequest(companyId)[0], {permission: newPermission}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});
      onSuccessCallback?.();
    },
  });

export const useRespondToPermissionUpgradeRequestMutation = ({companyId}: {companyId: number}) =>
  useMutation({
    mutationKey: COMPANY_KEYS.mCompanyMemberPermissionUpdateResponse(companyId),
    mutationFn: ({user_id, status}: {user_id: number; status: "ACCEPTED" | "REJECTED"}) =>
      client.post(COMPANY_KEYS.mCompanyMemberPermissionUpdateResponse(companyId)[0], {user_id, status}),
    onSuccess: response => {
      queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompanyMembers(companyId)});
      toast.success(i18n.t(`multi_account:${snakeCase(response.data.success)}`));
    },
  });

export const useCompanyQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions: TQueryOptions<TCompanyProfile>;
}) =>
  useGenericQuery<TCompanyProfile>({
    queryKey: COMPANY_KEYS.qCompany(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyCampaignsQuery = ({
  agencyId,
  companyId,
  status,
  queryOptions,
}: {
  agencyId?: number;
  companyId?: number;
  status: "active" | "open" | "complete" | "paused" | "drafts";
  queryOptions: TInfiniteQueryOptions<TDashboardCampaign, AxiosError, TQueryResultWithCount<TDashboardCampaign[]>>;
}) =>
  useGenericInfiniteQueryWithCount<TDashboardCampaign>({
    queryKey:
      !!agencyId && !companyId
        ? AGENCY_KEYS.qAgencyCampaigns({agencyId, status})
        : COMPANY_KEYS.qCompanyCampaigns({companyId, status}),
    ...queryOptions,
  });

export const useCompanyInvitesQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TQueryOptions<SubBrandInviteSerializer[]>;
}) =>
  useGenericQuery<SubBrandInviteSerializer[]>({
    queryKey: COMPANY_KEYS.qCompanyInvites(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyListQuery = ({
  params,
  queryOptions,
}: {
  params?: string;
  queryOptions?: TInfiniteQueryOptions<TSingleBrandCustomer>;
} = {}) =>
  useGenericInfiniteQuery<TSingleBrandCustomer>({
    queryKey: COMPANY_KEYS.qCompanyList(params),
    ...queryOptions,
  });

export const useCompanyMembersQuery = ({
  companyId,
  queryOptions,
}: {
  companyId: number;
  queryOptions?: TQueryOptions<TAccountMember[]>;
}) =>
  useGenericQuery<TAccountMember[]>({
    queryKey: COMPANY_KEYS.qCompanyMembers(companyId),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyRecommendedInfluencersQuery = ({
  companyId,
  queryOptions,
}: {
  companyId?: number;
  queryOptions?: TInfiniteQueryOptions<TGroupInfluencer>;
}) =>
  useGenericInfiniteQuery<TGroupInfluencer>({
    queryKey: COMPANY_KEYS.qCompanyRecommendedInfluencers(companyId!),
    enabled: !!companyId,
    ...queryOptions,
  });

export const useCompanyReferralsQuery = (companyId?: number) =>
  useGenericInfiniteQueryWithCount<ReferredInfluencerSerializer>({
    queryKey: COMPANY_KEYS.qCompanyReferrals(companyId!),
    enabled: !!companyId,
  });
